/**
 * How a live model is doing in production, as two bars sharing one scale.
 *
 * Share of leads on top, share of conversions below. The GAP between the two
 * rows is the reading: a model that separates puts a small slice of leads in
 * Very good and a large slice of conversions there, so the top bar's blue
 * stub sits under a wide blue band. A model that does not separate draws two
 * near-parallel bars, and that is legible at a glance down a column of twenty
 * rows in a way a single percentage never was.
 *
 * One bar was tried first and did not earn its width: a distribution alone
 * says how many accounts landed in each tier, which is a fact about the
 * thresholds rather than about whether the model works.
 *
 * The values live on hover rather than in a legend above the table. A legend
 * costs a row of chrome on every render to explain four colours that repeat
 * on every line; the hover carries the counts, the shares, the rate and the
 * slice the numbers are drawn on, which is more than a legend could.
 */
import React from 'react';

import { LivePerformance } from '../model/livePerformanceApi';
import './performanceBars.css';

/** Tier order, worst last, matching the ramp everywhere else in Studio. */
const SEGMENTS = ['very good', 'good', 'medium', 'low'] as const;

const SEGMENT_LABELS: Record<string, string> = {
  'very good': 'Very good',
  good: 'Good',
  medium: 'Medium',
  low: 'Low',
};

/** The class carrying each tier's colour, so the ramp is defined once in CSS. */
const SEGMENT_CLASS: Record<string, string> = {
  'very good': 'mhp-vg',
  good: 'mhp-good',
  medium: 'mhp-med',
  low: 'mhp-low',
};

/**
 * Shares as percentages of the total, and the total itself.
 *
 * Returns null rather than zeroes when nothing is counted: four 0% bars would
 * render as a flat empty track that reads like a measurement, when what it
 * means is that there is nothing to measure yet.
 */
export function sharesOf(
  values: Record<string, number>
): { shares: Record<string, number>; total: number } | null {
  const total = SEGMENTS.reduce(
    (sum, segment) => sum + (Number(values[segment]) || 0),
    0
  );
  if (!total) return null;
  const shares: Record<string, number> = {};
  SEGMENTS.forEach((segment) => {
    shares[segment] = ((Number(values[segment]) || 0) / total) * 100;
  });
  return { shares, total };
}

function Bar({
  shares,
  label,
}: {
  shares: Record<string, number>;
  label: string;
}) {
  return (
    <span
      className="mhp-bar"
      role="img"
      aria-label={`${label}: ${SEGMENTS.map(
        (segment) => `${SEGMENT_LABELS[segment]} ${shares[segment].toFixed(0)}%`
      ).join(', ')}`}
    >
      {SEGMENTS.map((segment) => (
        <span
          key={segment}
          className={SEGMENT_CLASS[segment]}
          style={{ width: `${shares[segment]}%` }}
        />
      ))}
    </span>
  );
}

const count = (value: number) => value.toLocaleString('en-US');
const share = (value: number) => `${value.toFixed(0)}%`;

export function PerformanceBars({ live }: { live: LivePerformance | null }) {
  const leadValues: Record<string, number> = {};
  const conversionValues: Record<string, number> = {};
  SEGMENTS.forEach((segment) => {
    leadValues[segment] = live?.funnel?.[segment]?.leads ?? 0;
    conversionValues[segment] = live?.funnel?.[segment]?.conversions ?? 0;
  });

  const leads = sharesOf(leadValues);
  const conversions = sharesOf(conversionValues);

  // Leads alone is not enough to draw: the second bar is the half that says
  // whether the model works, and one bar labelled "Leads" over an empty track
  // invites reading the distribution as the result.
  if (!leads || !conversions) {
    return <span className="mhp-none">No scoring data yet</span>;
  }

  return (
    <span className="mhp">
      <span className="mhp-bars">
        <span className="mhp-rl">Leads</span>
        <Bar shares={leads.shares} label="Share of leads" />
        <span className="mhp-rl">Conv</span>
        <Bar shares={conversions.shares} label="Share of conversions" />
      </span>

      <span className="mhp-pop" role="tooltip">
        <span className="mhp-ph">
          Live, last {live.periods?.length ?? 0} periods
        </span>
        <span className="mhp-grid">
          <span className="mhp-hd">Segment</span>
          <span className="mhp-hd mhp-r">Leads</span>
          <span className="mhp-hd mhp-r">Conv.</span>
          <span className="mhp-hd mhp-r">Rate</span>
          {SEGMENTS.map((segment) => (
            <React.Fragment key={segment}>
              <span className="mhp-seg">
                <i className={`mhp-sw ${SEGMENT_CLASS[segment]}`} />
                {SEGMENT_LABELS[segment]}
              </span>
              <span className="mhp-v mhp-r">
                {count(leadValues[segment])}
                <em>{share(leads.shares[segment])}</em>
              </span>
              <span className="mhp-v mhp-r">
                {count(conversionValues[segment])}
                <em>{share(conversions.shares[segment])}</em>
              </span>
              <span className="mhp-v mhp-r">
                {(live.rates?.[segment] ?? 0).toFixed(1)}%
              </span>
            </React.Fragment>
          ))}
        </span>
        <span className="mhp-tot">
          {count(leads.total)} leads · {count(conversions.total)} conversions
        </span>

        {/* Which population these numbers describe. Two models on one page can
            be drawn on different conversion goals, and without this the column
            reads as directly comparable when it is not. */}
        <span className="mhp-slice">
          <span className="mhp-k">Audience</span>
          <span className="mhp-val">{live.audience}</span>
          <span className="mhp-k">Conversion</span>
          <span className="mhp-val">{live.conversionType}</span>
          {/* Three states, not two. "Asked and matched" and "never asked" are
              the same absence in the two fields above, so a ✓ on both claimed
              an agreement nothing had checked. */}
          {live.requestedAudience || live.requestedConversionType ? (
            <span className="mhp-prov mhp-alt">
              <b>!</b>
              <span>
                Trained on{' '}
                <b>{live.requestedAudience ?? live.requestedConversionType}</b>,
                which production has no live data for.
              </span>
            </span>
          ) : null}
          {!live.requestedAudience &&
          !live.requestedConversionType &&
          live.trainedOnKnown ? (
            <span className="mhp-prov">
              <b>✓</b>
              <span>Same slice this model was trained on.</span>
            </span>
          ) : null}
          {!live.requestedAudience &&
          !live.requestedConversionType &&
          !live.trainedOnKnown ? (
            <span className="mhp-prov mhp-unknown">
              <b>?</b>
              <span>
                The slice this model trained on is not recorded, so this is the
                default reading rather than a match.
              </span>
            </span>
          ) : null}
        </span>
      </span>
    </span>
  );
}

export default PerformanceBars;
